import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  geoDistance,
  geoEqualEarth,
  geoGraticule10,
  geoOrthographic,
  geoPath,
} from "d3-geo";
import { feature } from "topojson-client";
import type { Topology, GeometryCollection } from "topojson-specification";
import world from "./data/world.json";
import { count, duration, num, str, type Row, Skeleton } from "./ui";
import { useTheme } from "./theme";

const darkColors = [
  "#a899ff",
  "#5ce0d2",
  "#5faaff",
  "#edac67",
  "#e986bc",
  "#badc78",
  "#d6d6ed",
  "#8cd1ff",
  "#dfbeff",
  "#ff988b",
  "#9ababa",
  "#dfca98",
];
const lightColors = [
  "#6850c6",
  "#067e73",
  "#216ccb",
  "#a95715",
  "#ac397e",
  "#517624",
  "#616185",
  "#147492",
  "#8d49b7",
  "#bc4432",
  "#42736f",
  "#897128",
];
const useChartColors = () =>
  useTheme().resolved === "light" ? lightColors : darkColors;
export interface Series {
  label: string;
  points: [number, number | null][];
}
export interface Location {
  latitude: number;
  longitude: number;
  label: string;
  streams: number;
  users: string[];
}
const compact = (n: number) =>
  Intl.NumberFormat(undefined, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(n);
export function sumSeries(series: Series[], label: string): Series[] {
  const values = new Map<number, number | null>();
  for (const s of series)
    for (const [t, v] of s.points)
      values.set(
        t,
        v === null ? (values.get(t) ?? null) : (values.get(t) ?? 0) + v,
      );
  return values.size
    ? [{ label, points: [...values].sort((a, b) => a[0] - b[0]) }]
    : [];
}
export function LineChart({
  series,
  unit = "",
  scale = 1,
  label,
  loading = false,
  filled = false,
}: {
  series: Series[];
  unit?: string;
  scale?: number;
  label: string;
  loading?: boolean;
  filled?: boolean;
}) {
  const id = useId().replaceAll(":", "");
  const colors = useChartColors();
  const [selection, setSelection] = useState<string[]>([]);
  const selectedLabels = selection.filter((name) =>
    series.some((s) => s.label === name),
  );
  const shown = (name: string) =>
    !selectedLabels.length || selectedLabels.includes(name);
  const data = useMemo(() => {
    const samples = new Map<number, Record<string, number | null>>();
    series.forEach((s, i) => {
      for (const [time, value] of s.points) {
        const row = samples.get(time) ?? { time };
        row[`s${i}`] = value === null ? null : value / scale;
        samples.set(time, row);
      }
    });
    return [...samples.values()].sort((a, b) => a.time! - b.time!);
  }, [series, scale]);
  const from = data[0]?.time ?? 0;
  const to = data.at(-1)?.time ?? 0;
  let latest = data.length - 1;
  while (latest > 0 && !series.some((_, i) => data[latest]?.[`s${i}`] != null))
    latest--;
  const index = Math.min(data.length - 1, Math.max(0, latest));
  const selected = data[index];
  const format = (value: number) =>
    `${Number(value.toFixed(1)).toLocaleString()}${unit ? ` ${unit}` : ""}`;
  const tick = (time: number) =>
    new Date(time * 1000).toLocaleString(
      undefined,
      to - from > 86400
        ? {
            month: "short",
            day: "numeric",
            ...(to - from > 366 * 86400 ? { year: "2-digit" as const } : {}),
          }
        : { hour: "numeric", minute: "2-digit" },
    );
  const fullTime = (time: number) =>
    new Date(time * 1000).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      ...(to - from > 366 * 86400 ? { year: "numeric" as const } : {}),
      hour: "numeric",
      minute: "2-digit",
    });
  const color = (i: number) => colors[(i + (filled ? 1 : 0)) % colors.length]!;
  if (loading && !data.length) return <Skeleton kind="chart" count={1} />;
  if (!data.length)
    return (
      <div className="chart-empty" role="status">
        No activity in this period.
      </div>
    );
  return (
    <div className="line-chart">
      <div className="chart-plot">
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <ComposedChart
            data={data}
            margin={{ top: 16, right: 16, bottom: 4, left: 0 }}
            accessibilityLayer
            aria-label={`${label}. Use left and right arrow keys to explore samples.`}
          >
            <defs>
              <linearGradient id={`area-${id}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color(0)} stopOpacity={0.25} />
                <stop offset="100%" stopColor={color(0)} stopOpacity={0.015} />
              </linearGradient>
            </defs>
            <CartesianGrid
              vertical={false}
              stroke="var(--chart-grid)"
              strokeDasharray="3 5"
            />
            <XAxis
              dataKey="time"
              type="number"
              domain={from === to ? [from, to + 1] : ["dataMin", "dataMax"]}
              {...(from === to ? { ticks: [from] } : {})}
              tickFormatter={tick}
              tick={{ fill: "var(--muted)", fontSize: 14 }}
              tickLine={false}
              axisLine={false}
              tickMargin={14}
              height={44}
              minTickGap={40}
              interval="preserveStartEnd"
            />
            <YAxis
              width={52}
              domain={[0, "auto"]}
              allowDecimals={scale !== 1}
              tickFormatter={compact}
              tick={{ fill: "var(--muted)", fontSize: 14 }}
              tickLine={false}
              axisLine={false}
              tickMargin={10}
              tickCount={5}
            />
            <Tooltip
              isAnimationActive={false}
              filterNull={false}
              wrapperStyle={{ pointerEvents: "auto", zIndex: 2 }}
              cursor={{ stroke: "var(--muted)", strokeDasharray: "4 4" }}
              content={({ active, label: time, payload }) =>
                active && payload.length ? (
                  <div className="chart-tooltip">
                    <time>{fullTime(Number(time))}</time>
                    {payload.map((entry) => (
                      <div key={String(entry.dataKey)}>
                        <i style={{ background: entry.color }} />
                        <span>{entry.name}</span>
                        <strong>
                          {entry.value == null
                            ? "No sample"
                            : format(Number(entry.value))}
                        </strong>
                      </div>
                    ))}
                  </div>
                ) : null
              }
            />
            {series.map((s, i) => {
              const common = {
                dataKey: `s${i}`,
                name: s.label,
                stroke: color(i),
                strokeWidth: 2.5,
                hide: !shown(s.label),
                connectNulls: false,
                isAnimationActive: false,
                dot:
                  s.points.filter(([, value]) => value !== null).length === 1,
                activeDot: { r: 5, stroke: "var(--panel)", strokeWidth: 2 },
              };
              return filled && series.length === 1 ? (
                <Area
                  key={s.label}
                  {...common}
                  type="linear"
                  fill={`url(#area-${id})`}
                />
              ) : (
                <Line key={s.label} {...common} type="linear" />
              );
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="chart-readout">
        <div className="chart-readout-heading">
          <time>
            Latest · {selected?.time != null ? fullTime(selected.time) : "—"}
          </time>
          {selectedLabels.length > 0 && (
            <button className="chart-reset" onClick={() => setSelection([])}>
              Show all
            </button>
          )}
        </div>
        <div className="chart-series">
          {series.map((s, i) => (
            <button
              key={s.label}
              aria-pressed={shown(s.label)}
              aria-label={`${s.label}: ${selected?.[`s${i}`] == null ? "No sample" : format(selected[`s${i}`]!)}. Select series; Control or Command click to select multiple`}
              onClick={(event) =>
                setSelection((current) => {
                  if (event.ctrlKey || event.metaKey) {
                    const active = current.filter((name) =>
                      series.some((item) => item.label === name),
                    );
                    return active.includes(s.label)
                      ? active.filter((name) => name !== s.label)
                      : [...active, s.label];
                  }
                  return current.length === 1 && current[0] === s.label
                    ? []
                    : [s.label];
                })
              }
            >
              <i style={{ background: color(i) }} />
              <span>{s.label}</span>
              <strong>
                {selected?.[`s${i}`] == null ? "—" : format(selected[`s${i}`]!)}
              </strong>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
export function Bars({
  rows,
  loading = false,
  metric = "plays",
  onSelect,
}: {
  rows: Row[];
  loading?: boolean;
  metric?: string;
  onSelect?: (label: string) => void;
}) {
  const colors = useChartColors();
  if (loading) return <Skeleton />;
  const data = rows.slice(0, 8);
  const max = Math.max(1, ...data.map((r) => num(r[metric])));
  return (
    <div className="bar-list">
      {data.length ? (
        data.map((r, i) => {
          const label = str(r.label) || "Unknown";
          const value =
            metric === "duration" ? duration(r[metric]) : count(r[metric]);
          const content = (
            <>
              <span className="bar-label">
                <span>{label}</span>
                <strong>{value}</strong>
              </span>
              <span className="bar-plot" aria-hidden="true">
                <ResponsiveContainer width="100%" height={10} minWidth={0}>
                  <BarChart
                    data={[{ value: num(r[metric]) }]}
                    layout="vertical"
                    margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
                    accessibilityLayer={false}
                  >
                    <XAxis type="number" domain={[0, max]} hide />
                    <YAxis type="category" hide />
                    <Bar
                      dataKey="value"
                      fill={colors[i % colors.length]}
                      radius={5}
                      background={{ fill: "var(--chart-grid)", radius: 5 }}
                      barSize={10}
                      isAnimationActive={false}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </span>
            </>
          );
          return onSelect ? (
            <button
              className="bar-row"
              key={label}
              onClick={() => onSelect(str(r.label))}
            >
              {content}
            </button>
          ) : (
            <div className="bar-row" key={label}>
              {content}
            </div>
          );
        })
      ) : (
        <p className="empty-note">No activity in this period.</p>
      )}
    </div>
  );
}
const topology = world as unknown as Topology<{
  countries: GeometryCollection;
  land: GeometryCollection;
}>;
const land = feature(topology, topology.objects.land);
const countries = feature(topology, topology.objects.countries);
export function WorldMap({ locations }: { locations: Location[] }) {
  const [mode, setMode] = useState("globe");
  const [rotation, setRotation] = useState<[number, number]>([95, -25]);
  const [zoom, setZoom] = useState(1);
  const [selected, setSelected] = useState<Location | null>(null);
  const [autoRotate, setAutoRotate] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [coasting, setCoasting] = useState(false);
  const velocity = useRef<[number, number]>([0, 0]);
  const motionReduced = useRef(false);
  const [visible, setVisible] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const container = useRef<HTMLDivElement>(null);
  const drag = useRef<{
    x: number;
    y: number;
    lon: number;
    lat: number;
    lastX: number;
    lastY: number;
    time: number;
  } | null>(null);
  const globeId = useId().replaceAll(":", "");
  useEffect(() => {
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updateMotion = () => {
      motionReduced.current = motion.matches;
      setAutoRotate(!motion.matches);
      if (motion.matches) {
        velocity.current = [0, 0];
        setCoasting(false);
      }
    };
    const updateVisibility = () => setPageVisible(!document.hidden);
    updateMotion();
    updateVisibility();
    motion.addEventListener("change", updateMotion);
    document.addEventListener("visibilitychange", updateVisibility);
    const observer = new IntersectionObserver(([entry]) =>
      setVisible(!!entry?.isIntersecting),
    );
    if (container.current) observer.observe(container.current);
    return () => {
      motion.removeEventListener("change", updateMotion);
      document.removeEventListener("visibilitychange", updateVisibility);
      observer.disconnect();
    };
  }, []);
  useEffect(() => {
    if (
      mode !== "globe" ||
      (!autoRotate && !coasting) ||
      dragging ||
      !visible ||
      !pageVisible
    )
      return;
    let frame = 0;
    let previous = 0;
    const animate = (time: number) => {
      if (!previous) previous = time;
      const elapsed = time - previous;
      // Bound geometry updates to 30fps, and avoid a jump after a stalled frame.
      if (elapsed >= 1000 / 30) {
        const dt = Math.min(elapsed, 64);
        const [vx, vy] = velocity.current;
        const moving = Math.hypot(vx, vy) > 0.001;
        const decay = Math.exp(-dt / 650);
        // Integrate decay so the fling behaves consistently across frame rates.
        const distance = 650 * (1 - decay);
        setRotation(([longitude, latitude]) => [
          (longitude + (moving ? vx * distance : autoRotate ? dt * 0.004 : 0)) %
            360,
          Math.max(-80, Math.min(80, latitude + (moving ? vy * distance : 0))),
        ]);
        velocity.current = moving ? [vx * decay, vy * decay] : [0, 0];
        if (!moving && coasting) setCoasting(false);
        previous = time;
      }
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [mode, autoRotate, coasting, dragging, visible, pageVisible]);
  const projection = useMemo(
    () =>
      mode === "globe"
        ? geoOrthographic()
            .rotate(rotation)
            .scale(148 * zoom)
            .translate([250, 166])
            .clipAngle(90)
        : geoEqualEarth().fitExtent(
            [
              [12, 12],
              [488, 320],
            ],
            { type: "Sphere" },
          ),
    [mode, rotation, zoom],
  );
  const path = geoPath(projection);
  const center: [number, number] = [-rotation[0], -rotation[1]];
  const stopMomentum = () => {
    velocity.current = [0, 0];
    setCoasting(false);
  };
  const endDrag = (cancelled = false) => {
    const last = drag.current;
    if (!last) return;
    drag.current = null;
    setDragging(false);
    if (
      cancelled ||
      motionReduced.current ||
      performance.now() - last.time > 100
    )
      stopMomentum();
    else setCoasting(Math.hypot(...velocity.current) > 0.001);
  };
  const focus = (p: Location) => {
    stopMomentum();
    setAutoRotate(false);
    setSelected(p);
    setRotation([-p.longitude, -p.latitude]);
  };
  const rotate = (degrees: number) => {
    stopMomentum();
    setAutoRotate(false);
    setSelected(null);
    setRotation(([longitude, latitude]) => [longitude + degrees, latitude]);
  };
  return (
    <div className="world-map" ref={container}>
      <div className="map-toolbar">
        <div className="segmented">
          {["globe", "map"].map((m) => (
            <button
              key={m}
              aria-pressed={mode === m}
              onClick={() => {
                stopMomentum();
                setMode(m);
              }}
            >
              {m === "globe" ? "Globe" : "Map"}
            </button>
          ))}
        </div>
        {mode === "globe" && (
          <button
            className="rotation-toggle"
            aria-label={
              autoRotate ? "Pause globe rotation" : "Start globe rotation"
            }
            aria-pressed={autoRotate}
            onClick={() => {
              stopMomentum();
              setAutoRotate((value) => !value);
              if (!autoRotate) setSelected(null);
            }}
          >
            <span aria-hidden="true">{autoRotate ? "Ⅱ" : "▷"}</span>
            {autoRotate ? "Pause" : "Rotate"}
          </button>
        )}
      </div>
      <div className="globe-stage">
        <svg
          className={mode === "globe" ? "globe-canvas" : "map-canvas"}
          viewBox="0 0 500 332"
          role="img"
          aria-label="Approximate playback locations. Choose a location below to explore."
          onPointerDown={(e) => {
            if (mode === "globe" && e.isPrimary && e.button === 0) {
              stopMomentum();
              setSelected(null);
              setDragging(true);
              drag.current = {
                x: e.clientX,
                y: e.clientY,
                lon: rotation[0],
                lat: rotation[1],
                lastX: e.clientX,
                lastY: e.clientY,
                time: performance.now(),
              };
              e.currentTarget.setPointerCapture(e.pointerId);
            }
          }}
          onPointerUp={() => endDrag()}
          onPointerCancel={() => endDrag(true)}
          onLostPointerCapture={() => endDrag(true)}
          onPointerMove={(e) => {
            const d = drag.current;
            if (d) {
              const now = performance.now();
              const dt = Math.max(8, now - d.time);
              const limit = (v: number) => Math.max(-0.45, Math.min(0.45, v));
              velocity.current = [
                limit(((e.clientX - d.lastX) * 0.4) / dt),
                limit((-(e.clientY - d.lastY) * 0.4) / dt),
              ];
              d.lastX = e.clientX;
              d.lastY = e.clientY;
              d.time = now;
              setRotation([
                d.lon + (e.clientX - d.x) * 0.4,
                Math.max(-80, Math.min(80, d.lat - (e.clientY - d.y) * 0.4)),
              ]);
            }
          }}
        >
          <defs>
            <radialGradient id={`ocean-${globeId}`} cx="35%" cy="25%" r="75%">
              <stop offset="0%" stopColor="var(--ocean-light)" />
              <stop offset="100%" stopColor="var(--ocean-dark)" />
            </radialGradient>
          </defs>
          <path
            d={path({ type: "Sphere" }) || ""}
            fill={`url(#ocean-${globeId})`}
            stroke="var(--globe-edge)"
            strokeWidth="1"
          />
          <path
            d={path(geoGraticule10()) || ""}
            fill="none"
            stroke="var(--globe-grid)"
            strokeWidth=".5"
          />
          <path
            d={path(land) || ""}
            fill="var(--land)"
            stroke="var(--land-edge)"
            strokeWidth=".5"
          />
          <path
            d={path(countries) || ""}
            fill="none"
            stroke="var(--country-edge)"
            strokeWidth=".3"
          />
          {locations.map((p) => {
            if (
              mode === "globe" &&
              geoDistance(center, [p.longitude, p.latitude]) > Math.PI / 2
            )
              return null;
            const xy = projection([p.longitude, p.latitude]);
            if (!xy) return null;
            return (
              <g key={`${p.latitude}:${p.longitude}`}>
                <circle
                  cx={xy[0]}
                  cy={xy[1]}
                  r={Math.min(15, 4 + Math.sqrt(p.streams))}
                  fill="var(--cyan)"
                  opacity=".17"
                />
                <circle
                  cx={xy[0]}
                  cy={xy[1]}
                  r={Math.min(6, 2 + Math.sqrt(p.streams) / 2)}
                  fill="var(--cyan)"
                  stroke="var(--point-edge)"
                  strokeWidth=".6"
                >
                  <title>
                    {p.label} · {p.streams} streams seen
                  </title>
                </circle>
              </g>
            );
          })}
        </svg>
        {mode === "globe" && (
          <div className="map-buttons" aria-label="Globe controls">
            <button aria-label="Rotate globe west" onClick={() => rotate(-30)}>
              ←
            </button>
            <button aria-label="Rotate globe east" onClick={() => rotate(30)}>
              →
            </button>
            <button
              aria-label="Zoom globe in"
              disabled={zoom >= 1.5}
              onClick={() => setZoom(Math.min(1.5, zoom + 0.25))}
            >
              +
            </button>
            <button
              aria-label="Zoom globe out"
              disabled={zoom <= 0.75}
              onClick={() => setZoom(Math.max(0.75, zoom - 0.25))}
            >
              −
            </button>
          </div>
        )}
      </div>
      {selected && (
        <p className="map-caption">
          <strong>{selected.label.replaceAll("None - ", "")}</strong>
          <br />
          {selected.users.join(", ")}
        </p>
      )}
      <div className="location-list">
        {locations.slice(0, 8).map((p) => (
          <button
            key={`${p.latitude}:${p.longitude}`}
            aria-pressed={
              selected?.latitude === p.latitude &&
              selected?.longitude === p.longitude
            }
            onClick={() => focus(p)}
          >
            <span>{p.label.replaceAll("None - ", "")}</span>
            <strong>{count(p.streams)}</strong>
          </button>
        ))}
      </div>
    </div>
  );
}
export function Heatmap({
  rows,
  metric = "plays",
}: {
  rows: Row[];
  metric?: string;
}) {
  const max = Math.max(1, ...rows.map((r) => num(r[metric])));
  const [selected, setSelected] = useState<number | null>(null);
  const names = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const content = (cell: number) => {
    const r = rows.find((x) => num(x.cell) === cell);
    return `${names[Math.floor(cell / 24)]} ${cell % 24}:00 · ${metric === "duration" ? duration(r?.duration) : `${count(r?.plays)} plays`}`;
  };
  return (
    <div className="heatmap">
      <div
        className="heatmap-scroll"
        tabIndex={0}
        role="region"
        aria-label="Viewing habits by day and hour"
      >
        <div className="heatmap-grid">
          <span />
          {Array.from({ length: 24 }, (_, i) => (
            <small key={i}>{i % 3 === 0 ? i : ""}</small>
          ))}
          {names.map((day, d) => (
            <div className="heatmap-week" key={day}>
              <small>{day}</small>
              {Array.from({ length: 24 }, (_, h) => {
                const cell = d * 24 + h,
                  value = num(rows.find((x) => num(x.cell) === cell)?.[metric]);
                return (
                  <button
                    key={h}
                    title={content(cell)}
                    aria-label={content(cell)}
                    aria-pressed={selected === cell}
                    onClick={() => setSelected(cell)}
                    style={{
                      background: `var(--heat-${value === 0 ? 0 : Math.max(1, Math.ceil((value / max) ** 0.6 * 4))})`,
                    }}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="heatmap-caption">
        <span>{selected === null ? "" : content(selected)}</span>
        <span>
          Less{" "}
          {Array.from({ length: 5 }, (_, i) => (
            <i key={i} style={{ background: `var(--heat-${i})` }} />
          ))}{" "}
          More
        </span>
      </div>
    </div>
  );
}
